import { POI_SOURCE_ID } from '@bikemap/js/settings'
import ClickableFeatures from './ClickableFeatures'
import Dragging from './Dragging'
import FeatureSelection from './FeatureSelection'
import MapFeature from './MapFeature'

const POI_SELECTION_LAYER_ADAPTION = {
    layout: {
        'icon-size': 1.2,
        'symbol-z-order': 'viewport-y',
    },
    maxzoom: 24,
}

const POI_ID_PROPERTY = 'poi_id'

const isPoiLayer = layer => layer.id.startsWith('bm-poi-')

/**
 * @typedef {Object} MapSourcePoi
 *
 * @property {"Feature"} type
 * @property {{ poi_id: number, category: string }} properties
 * @property {Object} geometry Coordinates as GeoJSON
 */

/**
 * Makes existing POI layers on the map editable.
 */
export default class EditablePois extends MapFeature {

    /** @type {string[]} */
    _originalPoiLayerIds = []

    /** @type {MapSourcePoi[]} */
    _selectedPois = []

    /** @type {FeatureSelection} */
    _poiSelection

    /** @type {ClickableFeatures} */
    _clickablePois

    /** @type {Dragging} */
    _draggingFeature

    constructor() {
        super()

        this._handlePoiClick = this._handlePoiClick.bind(this)
        this._handlePoiDragged = this._handlePoiDragged.bind(this)
        this._dispatchPoisVisibility = this._dispatchPoisVisibility.bind(this)
    }

    /**
     * Show a selection of POIs on the map.
     * @param {MapSourcePoi[]} pois
     */
    setSelectedPois(pois) {
        this._selectedPois = pois

        if (this._poiSelection) {
            this._poiSelection.setSelection(this._selectedPois)
        }
    }

    /**
     * Get all POIs that are currently visible on the map.
     * @returns {MapSourcePoi[]}
     */
    getVisiblePois() {
        if (!this._map) return []

        return this._map.queryRenderedFeatures({
            layers: this._originalPoiLayerIds,
        })
    }

    _onAdd(map) {
        // Map is ready, so we can get all existing POI layers and base this functionality on them
        this._originalPoiLayerIds = this._getPoiLayerIds()

        this._initPoiSelection().addTo(map)
        this._initClickablePois().addTo(map)
        this._initDragging().addTo(map)

        this._initPoisVisibilityEvents()
    }

    _onRemove() {
        this._map.off('zoomend', this._dispatchPoisVisibility)

        this._draggingFeature.remove()
        this._clickablePois.remove()
        this._poiSelection.remove()
    }

    /**
     * Collect IDs of all POI layers in the current map style.
     * @returns {import("maplibre-gl").AnyLayer[]}
     */
    _getPoiLayerIds() {
        return this._map.getStyle().layers.reduce((poiLayerIds, layer) => {
            if (isPoiLayer(layer)) {
                poiLayerIds.push(layer.id)
            }
            return poiLayerIds
        }, [])
    }

    /**
     * Add selected POIs map content.
     * @returns {FeatureSelection}
     */
    _initPoiSelection() {
        this._poiSelection = new FeatureSelection({
            sourceId: POI_SOURCE_ID,
            layerAdaption: POI_SELECTION_LAYER_ADAPTION,
            featureIdKey: POI_ID_PROPERTY,
        }, ...this._originalPoiLayerIds)

        this._poiSelection.setSelection(this._selectedPois)

        return this._poiSelection
    }

    /**
     * Make all POIs on the map clickable.
     * @returns {ClickableFeatures}
     */
    _initClickablePois() {
        this._clickablePois = new ClickableFeatures(
            ...this._originalPoiLayerIds,
            ...this._poiSelection.content.layerIds,
        )
        this._clickablePois.addEventListener('click', this._handlePoiClick)
        return this._clickablePois
    }

    /**
     * Make POI selection draggable.
     * @returns {Dragging}
     */
    _initDragging() {
        this._draggingFeature = new Dragging(
            this._poiSelection.content
        )
        this._draggingFeature.addEventListener('dragged', this._handlePoiDragged)
        return this._draggingFeature
    }

    /**
     * Inform the outside via events whether POI layers are currently visible on the map.
     */
    _initPoisVisibilityEvents() {
        this._map.once('sourcedata', () => {
            this._dispatchPoisVisibility()
            this._map.on('zoomend', this._dispatchPoisVisibility)
        })
    }

    /**
     * Dispatch a poisvisibility event with the current value.
     */
    _dispatchPoisVisibility() {
        const minZoom = this._map.getSource(POI_SOURCE_ID).minzoom || 0
        const currentZoom = this._map.getZoom()

        this._dispatchEvent('poisvisibility', {
            poisVisible: currentZoom >= minZoom,
        })
    }

    /**
     * Handle click on a feature of a POI layer.
     * @param {import("maplibre-gl").MapMouseEvent} e
     */
    _handlePoiClick(e) {
        this._dispatchEvent('poiclick', {
            mapPois: e.features,
        })
    }

    /**
     * Handle when a POI has been dragged.
     * @param {{ feature }} e
     */
    _handlePoiDragged(e) {
        this._dispatchEvent('poidragged', {
            mapPoi: e.feature,
        })
    }

}
